import type { EntitlementsDto } from '@osteopro/shared';
import type { QueryClient } from '@tanstack/react-query';
import { isApiError } from './api.js';
import { planKeys } from './query-keys.js';

/**
 * Cabinet **bloqué** : essai terminé sans souscription, ou abonnement expiré. Décision du
 * 30 septembre 2026, précisée le 11 octobre — tout est fermé, lecture et assistance comprises,
 * sauf la souscription et l'export du propriétaire ; les données sont effacées 90 jours après la
 * fin de l'essai. Le serveur refuse chaque requête de données (`402 subscription_expired`) ; le
 * front remplace l'application par l'écran de blocage, pour que le praticien lise **une** phrase
 * claire au lieu d'une pluie d'erreurs.
 *
 * L'état se lit dans les droits (`readOnly`), déjà chargés par la barre latérale. Ce module ne
 * garde rien lui-même : il dit seulement quelles adresses restent ouvertes, et relit les droits
 * quand le serveur annonce un blocage que le cache ignore encore.
 */

/** Code du refus serveur. Il remplace `subscription_read_only` depuis le 30 septembre 2026. */
export const SUBSCRIPTION_EXPIRED_CODE = 'subscription_expired';

export function isSubscriptionExpiredRefusal(error: unknown): boolean {
  return isApiError(error) && error.code === SUBSCRIPTION_EXPIRED_CODE;
}

/**
 * Adresses servies malgré le blocage. Chacune est une sortie, pas un confort :
 * - la page d'abonnement, parce que souscrire est précisément ce qu'on demande ;
 * - le retour de caisse, sans quoi celui qui vient de payer tomberait sur le mur avant que le
 *   rappel du prestataire n'ait rouvert le cabinet.
 * L'aide n'y est plus : le serveur ferme l'assistance à un cabinet bloqué (11 octobre 2026).
 * L'export se lance depuis l'écran de blocage lui-même.
 */
const OPEN_PATHS: readonly RegExp[] = [
  /^\/settings\/subscription\/?$/,
  /^\/abonnement\/(succes|annule)\/?$/,
];

/**
 * L'équipe de la plateforme n'a pas besoin d'exception ici : elle n'est jamais bloquée, `AccessGate`
 * la laisse passer avant même de lire l'adresse.
 */
export function isOpenWhileBlocked(location: string): boolean {
  return OPEN_PATHS.some((path) => path.test(location));
}

/**
 * Branché sur les deux caches de requêtes. Un refus `subscription_expired` alors que les droits en
 * cache disent le cabinet ouvert — l'essai s'est terminé en cours de séance — fait relire les
 * droits, et l'écran de blocage prend la place de l'application. Déjà bloqué, on ne relit pas : une
 * rafale de refus ne doit pas déclencher une rafale de lectures.
 */
export function reportSubscriptionRefusal(queryClient: QueryClient, error: unknown): void {
  if (!isSubscriptionExpiredRefusal(error)) return;
  const cached = queryClient.getQueryData<EntitlementsDto>(planKeys.entitlements());
  if (cached?.readOnly === true) return;
  void queryClient.invalidateQueries({ queryKey: planKeys.entitlements() });
}

/**
 * Date d'effacement en toutes lettres, dans la langue de l'interface : c'est une échéance qu'on lit
 * une fois et qu'il ne faut pas mal comprendre. Une date absente ou illisible rend `null`, et la
 * phrase qui la porte est omise plutôt que fausse.
 */
export function formatDeletionDate(iso: string | null, language: string): string | null {
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(date);
}
